// kontaktstatusSegment.ts — die Anzeige-Projektion des Kontaktstatus-Segments
// in den beiden Anlage-Masken.
//
// Der Product Owner will EINE Reihe (PO 09.09.2026):
//
//   [ Interessent | Kunde | Sonstige | Persönlicher Kontakt ]
//
// Dahinter liegen aber weiterhin ZWEI Formularfelder, und das ist kein Rest
// aus der alten Umsetzung, sondern die einzige Variante, die trägt:
//
//   `kontaktStatus`  geht als `statusName` an die API. Findet
//                    `ContactService.resolveRelations` dazu keine Zeile im
//                    ContactStatus-Katalog, löscht es den Wert ERSATZLOS —
//                    der Kontakt entsteht ohne Status, mit HTTP 201 und ohne
//                    Fehlermeldung. „Persönlicher Kontakt“ steht in keinem
//                    Katalog und darf dieses Feld deshalb nie erreichen.
//   `persoenlich`    ist ein reiner Sichtbarkeits-Schalter. Der Server setzt
//                    daraufhin accessLevel PRIVATE und die Verantwortlichkeit
//                    auf den Anleger; nach der Anlage ist er unveränderlich.
//
// Fachlich schließen die beiden sich auch nicht aus: ein persönlicher Kontakt
// IST Interessent, Kunde oder Sonstiges. Genau deshalb erscheint unter der
// vierten Option ein zweites Segment mit den drei Katalogwerten — das obere
// bleibt eine echte Entweder-Oder-Wahl (`role="radiogroup"` stimmt damit), und
// der Status geht beim Umschalten nicht verloren.
//
// Bewusst ohne React/DOM, damit die Regel per Vitest prüfbar ist — wie
// verantwortlichkeit/firmenverbindungenBuendeln.ts.

import { HAUPT_STATUS_OPTIONS } from "./sonstigeStatusKatalog";

/**
 * Der Anzeigewert der vierten Option. Er ist KEIN `ContactStatus` und gehört
 * deshalb weder in `HAUPT_STATUS_OPTIONS` noch in die `SONSTIGE_OPTIONS_*` —
 * dort wäre er ein Statuswert, der im Backend-Katalog fehlt.
 */
export const STATUS_PERSOENLICH = "Persönlicher Kontakt";

/** Der Status, den die Anlage ohne jede Auswahl schickt (so auch beide Payload-Builder). */
const STATUS_DEFAULT = "Interessent";

/**
 * Die Optionen des OBEREN Segments.
 *
 * Im Umwandlungs-Modus (`erlaubtPersoenlich === false`) fehlt die vierte:
 * der Kontakt existiert bereits, und `persoenlich` entsteht ausschließlich
 * bei der Anlage — PUT und PATCH strippen das Feld serverseitig.
 */
export function hauptStatusOptionen(erlaubtPersoenlich: boolean): string[] {
  return erlaubtPersoenlich
    ? [...HAUPT_STATUS_OPTIONS, STATUS_PERSOENLICH]
    : [...HAUPT_STATUS_OPTIONS];
}

/**
 * Welches Segment oben aktiv ist — projiziert aus beiden Feldern.
 *
 * `persoenlich` wird zeichengenau gegen „ja“ geprüft, weil beide Payload-Builder
 * es genauso lesen. Der Default „Interessent“ ist Pflicht und nicht Kosmetik:
 * `StatusSegmented` markiert schlicht nichts, wenn der Wert nicht in seiner
 * Optionsliste steht — ein leeres `kontaktStatus` ließe die Reihe ungewählt
 * aussehen, obwohl der Server „Interessent“ bekäme.
 *
 * Sonst wird hier nichts normalisiert: was im Feld steht, wird angezeigt.
 * Auf einen Katalogwert geprüft wird beim SCHREIBEN (`oberSegmentAuswahl`) —
 * und `contactToFormState` liefert ohnehin nur die drei Werte oder leer.
 */
export function oberSegmentWert(kontaktStatus: string, persoenlich: string): string {
  if (persoenlich === "ja") return STATUS_PERSOENLICH;
  return kontaktStatus || STATUS_DEFAULT;
}

/**
 * Was ein Klick im OBEREN Segment in die beiden Felder schreibt.
 *
 * Auf „Persönlicher Kontakt“ bleibt der bisher gewählte Status erhalten — der
 * Anwender verliert seine Auswahl nicht und sieht sie im zweiten Segment
 * wieder. Alles andere schaltet den Sichtbarkeits-Schalter ab.
 */
export function oberSegmentAuswahl(
  gewaehlt: string,
  bisherigerStatus: string,
): { kontaktStatus: string; persoenlich: string } {
  // Alles, was in `kontaktStatus` landet, muss ein Katalogwert sein — siehe
  // Kopf der Datei: ein unbekannter `statusName` wird serverseitig lautlos
  // verworfen. Geprüft wird deshalb der SCHREIBENDE Weg, und zwar beide
  // Quellen: die geklickte Option und der mitgegebene Bestand.
  const nurKatalog = (status: string): string =>
    HAUPT_STATUS_OPTIONS.includes(status) ? status : STATUS_DEFAULT;

  if (gewaehlt === STATUS_PERSOENLICH) {
    return { kontaktStatus: nurKatalog(bisherigerStatus), persoenlich: "ja" };
  }
  return {
    kontaktStatus: HAUPT_STATUS_OPTIONS.includes(gewaehlt)
      ? gewaehlt
      : nurKatalog(bisherigerStatus),
    persoenlich: "",
  };
}

/* ------------------------------------------------------------------ *
 * Mehrere Rollen unter „Sonstige"
 * ------------------------------------------------------------------ */

/** Der Anzeigewert der dritten Option — Abzweig in die Rollenauswahl. */
export const STATUS_SONSTIGE = "Sonstige";

/**
 * Trennzeichen der Rollenliste im Formularfeld `kontaktStatusRollen`.
 *
 * Semikolon, NICHT Komma: „Vermittler, Tippgeber" enthält selbst eins. Dasselbe
 * Zeichen benutzen `OptionChips` und `TagInput` — der flache `FormState`
 * (Record<string, string|number|boolean>) trägt mehrwertige Felder im Projekt
 * längst so.
 */
export const ROLLEN_SEP = "; ";

/** Rollenliste aus dem Formularfeld lesen. */
export function rollenAusFeld(wert: string): string[] {
  return eindeutig(
    (wert ?? "")
      .split(";")
      .map((r) => r.trim())
      .filter(Boolean),
  );
}

/** Rollenliste ins Formularfeld schreiben. */
export function rollenInsFeld(rollen: string[]): string {
  return eindeutig(rollen).join(ROLLEN_SEP);
}

/** Reihenfolge erhalten, Dubletten raus — „Halter" steht unter Schaden UND Risiko. */
function eindeutig(werte: string[]): string[] {
  return Array.from(new Set(werte.filter(Boolean)));
}

/**
 * Was die Anlage an die API schickt: EIN Primär-Status plus beliebig viele
 * Zusatz-Status.
 *
 * Die Regel steht hier und nicht in den Masken, weil an ihr zwei Dinge hängen,
 * die man nur am Datenmodell entscheiden kann:
 *
 *  - **Genau ein Primär-Status.** Listen, Filter, Kacheln und die RBAC-Sicht
 *    lesen ausschließlich `Contact.contactStatusId` (Filterblock in
 *    `ContactService`). Ein Kunde, der zugleich Lieferant ist, MUSS „Kunde" als
 *    Primär-Status behalten, sonst verschwindet er aus der Kundenliste. Deshalb
 *    gewinnt die Antwort auf „auch Kunde oder Interessent?" gegen jede Rolle.
 *  - **Nur Katalogwerte.** `ContactService.resolveRelations` löscht einen
 *    unbekannten `statusName` ERSATZLOS (HTTP 201, keine Meldung). Der
 *    Gruppenname einer Rollengruppe darf das Feld deshalb nie erreichen — und
 *    „Persönlicher Kontakt" schon gar nicht, der ist ein Schalter.
 *
 * Ohne Rolle und ohne Rückfrage bleibt es bei „Sonstige" — wie bisher.
 */
export function statusFuerApi(args: {
  kontaktStatus: string;
  rollen: string[];
  /** Antwort auf „Ist der Kontakt auch Kunde oder Interessent?" — leer erlaubt. */
  auchKundeOderInteressent?: string;
}): { statusName: string; weitereStatus: string[] } {
  const rollen = eindeutig(args.rollen ?? []);

  if (args.kontaktStatus !== STATUS_SONSTIGE) {
    // Außerhalb von „Sonstige" gibt es keine Rollen. Der Default deckt den
    // Fall ab, dass die sichtbare Vorauswahl nie angeklickt wurde.
    const direkt = args.kontaktStatus;
    return {
      statusName: HAUPT_STATUS_OPTIONS.includes(direkt) ? direkt : STATUS_DEFAULT,
      weitereStatus: [],
    };
  }

  const auch = args.auchKundeOderInteressent ?? "";
  if (auch === "Kunde" || auch === "Interessent") {
    return { statusName: auch, weitereStatus: rollen };
  }

  const [erste, ...rest] = rollen;
  return { statusName: erste || STATUS_SONSTIGE, weitereStatus: rest };
}

/**
 * Der Rückweg: gespeicherter Primär-Status + Zusatz-Status → Formularzustand.
 *
 * Gegenstück zu {@link statusFuerApi}, gebraucht beim Bearbeiten und beim
 * Umwandeln Person ↔ Firma. Ohne ihn verlöre jede Umwandlung die Nebenrollen
 * still — sie stehen in `ContactStatusLink`, nicht am Kontakt.
 */
export function rollenAusStatus(
  primaer: string,
  zusatz: string[] = [],
): { kontaktStatus: string; rollen: string[]; auchKundeOderInteressent: string } {
  const istHaupt = primaer === "Kunde" || primaer === "Interessent";
  // Alles, was kein Hauptstatus ist, ist eine Rolle unter „Sonstige" — auch
  // Werte, die kein Anlage-Formular anbietet (etwa „Altkunde" oder die aus den
  // Bereinigungs-Workflows). Sie gehen sonst beim Speichern verloren.
  const zusatzRollen = eindeutig(
    (zusatz ?? []).filter(
      (s) => s && s !== "Kunde" && s !== "Interessent" && s !== STATUS_SONSTIGE,
    ),
  );

  if (istHaupt) {
    if (!zusatzRollen.length) {
      return { kontaktStatus: primaer, rollen: [], auchKundeOderInteressent: "" };
    }
    return {
      kontaktStatus: STATUS_SONSTIGE,
      rollen: zusatzRollen,
      auchKundeOderInteressent: primaer,
    };
  }

  if (!primaer) {
    return { kontaktStatus: "", rollen: zusatzRollen, auchKundeOderInteressent: "" };
  }

  return {
    kontaktStatus: STATUS_SONSTIGE,
    rollen: eindeutig([
      ...(primaer === STATUS_SONSTIGE ? [] : [primaer]),
      ...zusatzRollen,
    ]),
    auchKundeOderInteressent: "",
  };
}
